import { useEffect, useRef, useState } from 'react'
import { NavLink } from 'react-router-dom'

interface Sport {
  key: string
  name: string
  fullName: string
  badgeClass: string
  to: string
  enabled: boolean
}

const SPORTS: Sport[] = [
  { key: 'mlb', name: 'MLB', fullName: 'Baseball', badgeClass: 'bg-blue-600 text-white', to: '/', enabled: true },
  { key: 'pga', name: 'PGA', fullName: 'Golf', badgeClass: 'bg-emerald-600 text-white', to: '/pga', enabled: true },
  { key: 'nba', name: 'NBA', fullName: 'Basketball', badgeClass: 'bg-orange-500 text-white', to: '#', enabled: false },
  { key: 'nhl', name: 'NHL', fullName: 'Hockey', badgeClass: 'bg-sky-700 text-white', to: '#', enabled: false },
  { key: 'nfl', name: 'NFL', fullName: 'Football', badgeClass: 'bg-amber-700 text-white', to: '#', enabled: false },
]

const HOVER_EXPAND_DELAY_MS = 140
const HOVER_COLLAPSE_DELAY_MS = 180
/** Matches collapsed rail width — sport badges stay on this x axis while labels fade in. */
const ICON_RAIL_CLASS = 'flex w-14 shrink-0 items-center justify-center'

interface Props {
  activeSport?: string
}

export function Sidebar({ activeSport = 'mlb' }: Props) {
  const [hoverExpanded, setHoverExpanded] = useState(false)
  const expandRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  const collapseRef = useRef<ReturnType<typeof setTimeout> | null>(null)

  const [showLabels, setShowLabels] = useState(false)
  useEffect(() => {
    if (!hoverExpanded) {
      setShowLabels(false)
      return
    }
    const t = setTimeout(() => setShowLabels(true), 180)
    return () => clearTimeout(t)
  }, [hoverExpanded])

  const clearTimers = () => {
    if (expandRef.current) {
      clearTimeout(expandRef.current)
      expandRef.current = null
    }
    if (collapseRef.current) {
      clearTimeout(collapseRef.current)
      collapseRef.current = null
    }
  }

  useEffect(() => () => clearTimers(), [])

  const onEnter = () => {
    if (collapseRef.current) {
      clearTimeout(collapseRef.current)
      collapseRef.current = null
    }
    if (hoverExpanded || expandRef.current) return
    expandRef.current = setTimeout(() => {
      setHoverExpanded(true)
      expandRef.current = null
    }, HOVER_EXPAND_DELAY_MS)
  }

  const onLeave = () => {
    if (expandRef.current) {
      clearTimeout(expandRef.current)
      expandRef.current = null
    }
    if (collapseRef.current) return
    collapseRef.current = setTimeout(() => {
      setHoverExpanded(false)
      collapseRef.current = null
    }, HOVER_COLLAPSE_DELAY_MS)
  }

  return (
    <aside
      onMouseEnter={onEnter}
      onMouseLeave={onLeave}
      className={[
        'fixed inset-y-0 left-0 z-50 flex flex-col border-r border-slate-200 bg-white',
        'transition-[width,box-shadow] duration-200 ease-out',
        hoverExpanded ? 'w-52 shadow-2xl shadow-slate-900/15' : 'w-14',
      ].join(' ')}
    >
      <div className="flex h-16 shrink-0 items-center border-b border-slate-100">
        <div className={ICON_RAIL_CLASS}>
          <span className="rounded bg-slate-900 px-2 py-1 text-xs font-bold text-cyan-300">sm</span>
        </div>
        <span
          className={[
            'min-w-0 overflow-hidden whitespace-nowrap pr-3 text-sm font-semibold text-slate-900',
            showLabels ? 'block' : 'hidden',
          ].join(' ')}
        >
          sim-monster
        </span>
      </div>

      <nav className="flex min-h-0 flex-1 flex-col overflow-hidden py-3">
        <ul className="min-h-0 flex-1 space-y-1 overflow-y-auto overflow-x-hidden">
          {SPORTS.map((s) => (
            <SidebarItem key={s.key} sport={s} isActive={s.key === activeSport} showLabel={showLabels} />
          ))}
        </ul>
      </nav>

      <div className="relative flex shrink-0 border-t border-slate-100 py-3">
        <div className={ICON_RAIL_CLASS} aria-hidden />
        <p
          className={[
            'pointer-events-none absolute left-14 right-0 top-1/2 min-w-0 -translate-y-1/2 pr-3 text-[11px] text-slate-400',
            showLabels ? 'block' : 'hidden',
          ].join(' ')}
        >
          More leagues coming soon.
        </p>
      </div>
    </aside>
  )
}

function SidebarItem({
  sport,
  isActive,
  showLabel,
}: {
  sport: Sport
  isActive: boolean
  showLabel: boolean
}) {
  const Body = (
    <div
      className={[
        'group relative flex min-h-12 items-center',
        showLabel && sport.enabled && isActive ? 'bg-slate-100' : '',
        showLabel && sport.enabled ? 'hover:bg-slate-50' : '',
        sport.enabled
          ? isActive
            ? 'font-semibold text-slate-900'
            : 'text-slate-700'
          : 'cursor-not-allowed text-slate-400',
      ].join(' ')}
    >
      <div
        className={[
          ICON_RAIL_CLASS,
          'py-2',
          !showLabel && sport.enabled && isActive ? 'bg-slate-100' : '',
        ].join(' ')}
      >
        <span
          className={[
            'flex h-8 w-8 shrink-0 items-center justify-center rounded-md text-[10px] font-bold tracking-wide',
            sport.enabled ? sport.badgeClass : 'bg-slate-200 text-slate-400',
          ].join(' ')}
        >
          {sport.name}
        </span>
      </div>

      <div
        className={[
          'flex min-w-0 flex-1 flex-col justify-center py-2 pr-3 text-left',
          showLabel ? 'block' : 'hidden',
        ].join(' ')}
      >
        <span className="whitespace-nowrap text-sm leading-tight">{sport.name}</span>
        <span className="whitespace-nowrap text-[11px] leading-tight text-slate-400">
          {sport.enabled ? sport.fullName : `${sport.fullName} · soon`}
        </span>
      </div>

      {!showLabel && (
        <span className="invisible absolute left-full ml-2 whitespace-nowrap rounded bg-slate-900 px-2 py-1 text-xs text-white opacity-0 shadow-md transition group-hover:visible group-hover:opacity-100">
          {sport.name}
          {!sport.enabled ? ' (coming soon)' : ''}
        </span>
      )}
    </div>
  )

  return (
    <li className="relative">
      {sport.enabled ? <NavLink to={sport.to}>{Body}</NavLink> : <div aria-disabled="true">{Body}</div>}
    </li>
  )
}
